import {
  Icon,
  IconProps,
  Popover,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  TonicProps,
  useColorMode,
} from '@tonic-one/react';
import { InfoIcon, InfoOIcon, InfoSIcon } from '@tonic-one/react-icons';
import { ReactNode, useMemo } from 'react';

export interface InfoProps {
  content: ReactNode;
  placement?: PopoverProps['placement'];
  contentStyle?: any;
  customLabel?: any;
  variant?: 'outline' | 'solid' | 'small';
  iconStyle?: IconProps;
}

export function Info(
  props: InfoProps & Omit<TonicProps, 'content' | 'placement' | 'variant'>
) {
  const {
    placement = 'top',
    variant = 'outline',
    content,
    contentStyle = {},
    iconStyle = {},
    ...tonicProps
  } = props;
  const [colorMode] = useColorMode();
  const color = (
    {
      dark: 'white:secondary',
      light: 'black:secondary',
    } as const
  )[colorMode];
  const hoverColor = (
    {
      dark: 'white:primary',
      light: 'black:primary',
    } as const
  )[colorMode];
  const activeColor = color;
  const focusColor = color;
  const focusHoverColor = hoverColor;
  const focusActiveColor = activeColor;

  const iconComponent = useMemo(() => {
    const iconProps = {
      width: '16px',
      height: '16px',
      _hover: {
        color: hoverColor,
      },
      _active: {
        color: activeColor,
      },
      _focus: {
        color: focusColor,
      },
      _focusHover: {
        color: focusHoverColor,
      },
      _focusActive: {
        color: focusActiveColor,
      },
      color,
      ...iconStyle,
    };
    return (
      {
        outline: <Icon as={InfoOIcon} {...iconProps} />,
        solid: <Icon as={InfoIcon} {...iconProps} />,
        small: <Icon as={InfoSIcon} {...iconProps} />,
      } as const
    )[variant];
  }, [
    activeColor,
    color,
    focusActiveColor,
    focusColor,
    focusHoverColor,
    hoverColor,
    iconStyle,
    variant,
  ]);

  return (
    <Popover placement={placement} trigger="hover">
      <PopoverTrigger cursor="default" {...tonicProps}>
        {iconComponent}
      </PopoverTrigger>
      <PopoverContent
        maxWidth="400px"
        whiteSpace="pre-wrap"
        PopperProps={{
          modifiers: [
            {
              // https://popper.js.org/docs/v2/modifiers/flip/
              name: 'flip',
              enabled: true,
            },
          ],
          usePortal: true,
        }}
        {...contentStyle}
      >
        {content}
      </PopoverContent>
    </Popover>
  );
}
